import { UserInfoInterface } from 'types/user';

export interface PrefixAchievementsLinkParams {
  loadingInfo: boolean;
  isRede: boolean;
  userInfo?: UserInfoInterface;
  tasksManager?: any;
  tasksDuplaManager?: any;
}

export interface TaskCyclesVisibilityParams extends PrefixAchievementsLinkParams {
  cycleIsClosed: boolean;
}

function hasItems(list?: any): boolean {
  return Array.isArray(list) && list.length > 0;
}

// Gestor que participa da escada de apreciação (Validação → Nível 1/2/3). O `cod_etapa`
// é o mesmo critério que TaskCycles usa para montar a seção do gestor, e por isso ele
// entra aqui: sem ele, gestores fora da escada (cod_etapa 99, sentinela da base) abririam
// a caixa do dashboard sem nenhum conteúdo dentro.
export function isAppreciationLadderManager(userInfo?: UserInfoInterface): boolean {
  return (
    !!userInfo?.is_manager &&
    typeof userInfo.cod_etapa === 'number' &&
    userInfo.cod_etapa <= 5 &&
    userInfo.tipo_de_acordo === 'HIBRIDO'
  );
}

// Substituto com nível de apreciação pelo `cod_etapa_sub` (regra de 2026-10-21, A2): quem está
// substituindo um gestor (`substituindo` SIM e `cod_etapa_sub` 1..5) precisa chegar à
// lateralidade mesmo sem nível próprio — o assessor cod 0, `is_manager` false. O sub não dá
// nível na própria tela; dá só o caminho para apreciar no lugar do substituído. A home, o
// Header e a seção usam esta função para carregar a busca lateral e abrir o bloco do gestor.
export function isSubstitutoComNivel(userInfo?: UserInfoInterface): boolean {
  return (
    !!userInfo &&
    userInfo.substituindo === 'SIM' &&
    typeof userInfo.cod_etapa_sub === 'number' &&
    userInfo.cod_etapa_sub >= 1 &&
    userInfo.cod_etapa_sub <= 5
  );
}

// Decide se a seção "Realizações Destaque" (TaskCycles) aparece na home.
//
// O gate era guiado por realização: quem não tivesse nenhuma realização roteada para si
// simplesmente não via a seção. Isso escondia a área INTEIRA do gestor que ainda não
// recebeu realização alguma na fase — nem o botão de apreciar, nem o aviso de que não há
// nada pendente, que o próprio TaskCycles já sabe exibir. `isAppreciationLadderManager`
// cobre esse caso.
//
// As checagens de HIBRIDO/substituindo continuam porque não são redundantes: elas ainda
// respondem por gestores FORA da escada (cod_etapa > 5), preservando o que eles já viam.
export function shouldShowTaskCyclesSection({
  loadingInfo,
  cycleIsClosed,
  isRede,
  userInfo,
  tasksManager,
  tasksDuplaManager,
}: TaskCyclesVisibilityParams): boolean {
  if (cycleIsClosed) {
    return false;
  }

  return participatesInTaskCycles({ loadingInfo, isRede, userInfo, tasksManager, tasksDuplaManager });
}

// Decide se o item de menu "Realizações do seu prefixo" aparece na home.
//
// Mesma elegibilidade da seção, MENOS o ciclo fechado: a listagem do prefixo é histórica e
// continua fazendo sentido depois que o ciclo fecha — o usuário quer consultar o que a
// unidade entregou, e o `Detail` tem seletor de ciclo próprio. A seção TaskCycles é que não
// sobrevive ao fechamento, porque a home troca por `TaskCyclesClosed`.
//
// Ciclos v2/v3 não precisam de guarda aqui: o Dashboard redireciona para `/v2/` ou `/v3/`
// quando o ciclo selecionado tem `version` (ver `verifyVersionInit`), e aqueles Dashboards
// não têm este item de menu.
export function shouldShowPrefixAchievementsLink(params: PrefixAchievementsLinkParams): boolean {
  return participatesInTaskCycles(params);
}

// Elegibilidade do jogador à dinâmica de Realizações Destaque, independente do ciclo.
function participatesInTaskCycles({
  loadingInfo,
  isRede,
  userInfo,
  tasksManager,
  tasksDuplaManager,
}: PrefixAchievementsLinkParams): boolean {
  if (loadingInfo || isRede || !userInfo) {
    return false;
  }

  const isHibrido = userInfo.tipo_de_acordo === 'HIBRIDO';

  return (
    (userInfo.cad_missao === 1 && isHibrido) ||
    isAppreciationLadderManager(userInfo) ||
    hasItems(tasksManager) ||
    hasItems(tasksDuplaManager) ||
    (typeof userInfo.team_hibrido === 'number' && userInfo.team_hibrido > 0) ||
    (!!userInfo.is_manager && isHibrido) ||
    (!!userInfo.is_manager && userInfo.substituindo === 'SIM' && isHibrido) ||
    isSubstitutoComNivel(userInfo)
  );
}

// Áreas da seção "Realizações Destaque" (TaskCycles) na home.
//
// Cadastro: quem cadastra (`canRegister`, hoje = `cad_missao`) ou tem acordo predefinido.
// Apreciação: gestor da escada (`is_manager` e `cod_etapa` <= 5) ou substituto com nível (A2).
// Quando as DUAS aparecem para a mesma pessoa (ex.: assessora cod 0 / sub 3 substituindo, ou
// gestor que também cadastra), a home repetia a "Agenda de Apreciação" nas duas áreas e nada
// separava os blocos (pedido do Daniel, 2026-10-22): a área de cadastro omite a agenda, que
// fica só na de apreciação, e uma linha divide as duas. Com uma área só, nada muda.
export interface HomeAreasParams {
  canRegister?: boolean;
  predefinedAgreementType?: boolean;
  playerInfo?: UserInfoInterface;
  lateralManagers?: unknown[];
}

export function isRegistrationAreaVisible({ canRegister, predefinedAgreementType }: HomeAreasParams): boolean {
  return !!canRegister || !!predefinedAgreementType;
}

// Gestor da escada pelo critério do bloco: `is_manager` e `cod_etapa` <= 5 (mesma coerção do JS
// que o JSX fazia inline). Difere de `isAppreciationLadderManager`, que ainda exige HIBRIDO.
function isLadderManagerForHome(playerInfo?: UserInfoInterface): boolean {
  const codEtapa = Number(playerInfo?.cod_etapa);

  return !!playerInfo?.is_manager && codEtapa <= 5;
}

// Substituto SEM nível próprio (assessor cod 0 com cod_etapa_sub 1..5): só aprecia pela
// lateralidade. Os botões que navegam com o id dele ("Apreciar/Acompanhar realizações") caem
// no gate da apreciação sem acesso, porque a elegibilidade é resolvida pelo próprio id
// (HOM-06, 2026-10-22; casos F2123413 e F1725729 em staging).
export function isSubstituteWithoutOwnLevel(playerInfo?: UserInfoInterface): boolean {
  return isSubstitutoComNivel(playerInfo) && !isLadderManagerForHome(playerInfo);
}

// Apreciação: gestor da escada sempre; substituto sem nível próprio só quando a busca lateral
// trouxe alguém para substituir — sem ninguém, o bloco seria um beco sem saída (HOM-06).
export function isAppreciationAreaVisible(playerInfo?: UserInfoInterface, lateralManagers?: unknown[]): boolean {
  if (isLadderManagerForHome(playerInfo)) {
    return true;
  }

  return isSubstitutoComNivel(playerInfo) && hasItems(lateralManagers);
}

export function showsBothHomeAreas(params: HomeAreasParams): boolean {
  return isRegistrationAreaVisible(params) && isAppreciationAreaVisible(params.playerInfo, params.lateralManagers);
}
